import React, { useState, useEffect } from "react";
import * as yup from "yup";
import api from "../../axios/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BulkUserModal from "./BulkUserModal";
import Loader from "../common/Loader";
import Button from "../common/styled components/Button";
import Grid from "../common/Grid";

// Validation schema
const validationSchema = yup.object({
  userName: yup
    .string()
    .required("Name is required")
    .min(2, "Name should be at least 2 characters"),
  userEmail: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(6, "Password should be at least 6 characters"),
  role: yup
    .string()
    .oneOf(["individual", "admin"], "Please select a valid role")
    .required("Role is required"),
});

const initialFormState = {
  userName: "",
  userEmail: "",
  password: "",
  role: "individual",
};

const CreateUser = () => {
  const [open, setOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const handleClickOpen = () => setOpen(true);

  const [isBulkUserModalOpen, setIsBulkUserModalOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormState);
    setErrors({});
    setTouched({});
  };

  useEffect(() => {
    const fetchAdmins = async () => {
      setIsLoading(true); // Set loading state to true
      try {
        const response = await api.get("/api/admin/getUsers");
        const adminsData = response.data.users.filter(
          (user) => user.role_code === "admin"
        );
        setAdmins(adminsData);
      } catch (error) {
        console.error("Error fetching admins:", error);
        toast.error("Failed to fetch admin data");
      }finally {
        setIsLoading(false); // Set loading state to false after fetching
      }
    };

    fetchAdmins();
  }, []);

  const validateField = async (name, value) => {
    try {
      await validationSchema.validateAt(name, { [name]: value });
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, formData[name]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      userName: true,
      userEmail: true,
      password: true,
      role: true,
    });

    try {
      setIsLoading(true); // Set loading state to true
      await validationSchema.validate(formData, { abortEarly: false });
      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Unauthorized: No token found");
        return;
      }

      await api.post(
        "/api/admin/createUser",
        {
          ...formData,
          userEmail: formData.userEmail.toLowerCase(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("User created successfully");
      handleClose();
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else {
        console.error("Error creating user:", error);
        toast.error("Failed to create user");
      }
    }finally{
      setIsLoading(false); // Set loading state to false after fetching
    }
  };

  return (
    
    <Box sx={{ mt: 0, pt: 0 }}>
      <ToastContainer />
      {isLoading && (
          <div className="loading-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Loader />
          </div>
        )}
        <div className={isLoading ? 'content-disabled' : ''} style={{
          pointerEvents: isLoading ? 'none' : 'auto',
          opacity: isLoading ? 0.6 : 1,
          transition: 'opacity 0.3s'
        }}>
     <Button
  variant="contained"
  startIcon={<AddIcon />}
  onClick={handleClickOpen}
  sx={{
    backgroundColor: '#4CAF50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#43a047', // slightly darker green
      transform: 'scale(1.03)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
    transition: 'all 0.3s ease',
  }}
>
  Create New User
</Button>

<Button
  variant="contained"
  startIcon={<AddIcon />}
  onClick={() => setIsBulkUserModalOpen(true)}
  sx={{
    ml: 3,
    backgroundColor: '#4CAF50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#43a047',
      transform: 'scale(1.03)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
    transition: 'all 0.3s ease',
  }}
>
  Create Multiple Users
</Button>

      <BulkUserModal 
  open={isBulkUserModalOpen} 
  onClose={() => setIsBulkUserModalOpen(false)} 
        />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create New User
          <br />
        </DialogTitle>
        <p style={{ fontSize: "13px", padding: "0px 24px" }}>
          NOTE: fields marked with <span style={{ color: "red" }}>*</span> are
          mandatory to input.
        </p>

        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                id="userName"
                name="userName"
                label="Name *"
                value={formData.userName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.userName && Boolean(errors.userName)}
                helperText={touched.userName && errors.userName}
                multiline={true}
                rows={1}
              />

              <TextField
                fullWidth
                id="userEmail"
                name="userEmail"
                label="Email *"
                value={formData.userEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.userEmail && Boolean(errors.userEmail)}
                helperText={touched.userEmail && errors.userEmail}
                multiline={true}
                rows={1}
              />

              <TextField
                fullWidth
                id="password"
                name="password"
                label="Password *"
                type="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />

              <TextField
                fullWidth
                id="role"
                name="role"
                select
                label="Role *"
                value={formData.role}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.role && Boolean(errors.role)}
                helperText={touched.role && errors.role}
              >
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Grid
        columns={[
          {
            header: "Username",
            accessor: "full_name",
            sortable: true,
          },
          {
            header: "Email",
            accessor: "userEmail",
            sortable: true,
          },
          {
            header: "Verified Trees",
            accessor: "verifiedTrees",
            sortable: true,
            align: "center",
            render: (val) => val ?? 0,
          },
          {
            header: "Last Login",
            accessor: "lastLogin",
            sortable: true,
            align: "center",
            render: (val) => (val ? new Date(val).toLocaleString() : "-"),
          },
          {
            header: "Total Posts",
            accessor: "totalTrees",
            sortable: true,
            align: "center",
            render: (val) => val ?? 0,
          },
          {
            header: "Geolocated Count",
            accessor: "geoLocatedTreesCount",
            sortable: true,
            align: "center",
            render: (val) => val ?? 0,
          },
          {
            header: "Role",
            accessor: "role_code",
            sortable: true,
          },
          {
            header: "Deleted Posts",
            accessor: "deletedTrees",
            sortable: true,
            align: "center",
            render: (val) => val ?? 0,
          },
        ]}
        data={admins}
        variant="navy"
        pagination={true}
        pageSize={10}
        pageSizeOptions={[5, 10, 20, 50]}
        rowKey="id"
        emptyMessage="No users found"
      />
      </div>
    </Box>
  );
};

export default CreateUser;
